import { Subject as CacheInvalidationSubject } from 'rxjs';
import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { extractUserFacingErrorDetail } from './billing-error-utils';
import { ArchitecturalPlanPage } from './models/architectural-plan-page.model';
import {
  ArchitecturalPlanServiceClient,
  ArchitecturalPlanReviewServiceClient,
} from '../../generated.commonjs/ApiServiceClientPb';
import { ArchitecturalPlanAnalysisServiceClient } from '../../generated.commonjs/Analysis_availabilityServiceClientPb';
import { IccBookInfoServiceClient } from '../../generated.commonjs/Icc.infoServiceClientPb';
import { CodeApplicabilityServiceClient } from '../../generated.commonjs/Code_applicabilityServiceClientPb';
import { CrossReferenceServiceClient } from '../../generated.commonjs/Cross_referenceServiceClientPb';
import {
  BuildCrossReferenceGraphRequest,
  BuildCrossReferenceGraphResponse,
  BuildElementRegistryRequest,
  BuildElementRegistryResponse,
  GetCrossReferenceGraphRequest,
  GetCrossReferenceGraphResponse,
  GetElementRegistryRequest,
  GetElementRegistryResponse,
  RunCrossExaminationRequest,
  RunCrossExaminationResponse,
  GetCrossExaminationRequest,
  GetCrossExaminationResponse,
} from '../../generated.commonjs/cross_reference_pb';
import {
  GetArchitecturalPlanPageRequest,
  GetIccBookTableOfContentsRequest,
  GetApplicableCodeSectionsRequest,
  GetArchitecturalPlanRequest,
  UpdateArchitecturalPlanPageMetadataRequest,
  UpdateArchitecturalPlanPageMetadataResponse,
  GroomArchitecturalPlanPageMetadataRequest,
  GroomArchitecturalPlanPageMetadataResponse,
  GetRegionHierarchyRequest,
  GetRegionHierarchyResponse,
} from '../../generated.commonjs/api_pb';
import {
  ComplianceRiskBucket,
  FileComplianceCoverageState,
  FileComplianceSummary,
  FileOverviewSummary,
  GetFileComplianceSummaryRequest,
  GetFileOverviewSummaryRequest,
  GetPageComplianceReportRequest,
  ListFileCompliancePagesRequest,
  ListFileCompliancePagesResponse,
} from '../../generated.commonjs/plan.reviewer_pb';
import { GetAvailableAnalysisRequest } from '../../generated.commonjs/analysis_availability_pb';
import { GetMultipleIccBookInfoRequest, GetAvailableBookInfoRequest } from '../../generated.commonjs/icc.info_pb';
import { StartCodeApplicabilityAnalysisRequest } from '../../generated.commonjs/code_applicability_pb';
import { StartPageSectionComplianceReportTaskRequest } from '../../generated.commonjs/compliance_report_pb';
import { Chapters, Chapter, SubSection } from '../../generated.commonjs/icc.chapters_pb';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor, RpcAuthenticationError } from './auth.interceptor';
import { GrpcWrapperService } from './grpc-wrapper.service';

/**
 * Custom error class to preserve gRPC status codes when re-throwing errors.
 */
export class GrpcServiceError extends Error {
  code: number;

  constructor(message: string, code: number) {
    super(message);
    this.name = 'GrpcServiceError';
    this.code = code;
  }
}

/**
 * Categorized error information for better user experience.
 * Phase 3 enhancement for graceful error handling.
 */
export interface ComplianceErrorCategory {
  type:
    | 'ANALYSIS_NOT_AVAILABLE'
    | 'BACKEND_INITIALIZATION'
    | 'PERMISSION_DENIED'
    | 'NOT_FOUND'
    | 'SERVICE_UNAVAILABLE'
    | 'GRPC_ERROR'
    | 'NETWORK_ERROR';
  severity: 'info' | 'warning' | 'error';
  userMessage: string;
  technicalMessage: string;
  canRetry: boolean;
  showFallbackUI: boolean;
}

// Enhanced gRPC error detection for ESPv2 responses
interface GrpcErrorResponse {
  code: number;
  message: string;
  details?: any[];
}

// Code book configuration
export interface CodeBook {
  id: string;
  document_slug: string;
  title: string;
  abbreviation: string;
  year: string;
  available: boolean; // Whether this code book is available on the backend
}

// Code books configuration
// To enable a code book, set 'available: true' after confirming the backend has the necessary data
const SUPPORTED_CODE_BOOKS: CodeBook[] = [
  {
    id: '2217',
    document_slug: 'IBC2021P2',
    title: 'International Building Code (IBC)',
    abbreviation: 'IBC',
    year: '2021',
    available: true, // IBC is confirmed to be available
  },
  {
    id: '3123',
    document_slug: 'NJBC2021P1',
    title: 'New Jersey Building Code',
    abbreviation: 'NJBC',
    year: '2021',
    available: false, // TODO: Set to true when backend data is ready
  },
  {
    id: '3815',
    document_slug: 'FLBC2023P2',
    title: 'Florida Building Code',
    abbreviation: 'FLBC',
    year: '2023',
    available: true, // TODO: Set to true when backend data is ready
  },
  {
    id: '3100',
    document_slug: 'icca117-12017P4',
    title: 'ICC A117.1 Accessible and Usable Buildings and Facilities',
    abbreviation: 'ICCA117.1',
    year: '2017',
    available: true,
  },
];

// For now, use the first (and only) book as default
const DEFAULT_CODE_BOOK = SUPPORTED_CODE_BOOKS[0];

/** Central gRPC facade for plan review workflows: fetching plans, pages, compliance reports, and ICC book data. */
@Injectable({
  providedIn: 'root',
})
export class PlanReviewerService {
  private environmentService = inject(EnvironmentService);
  readonly textCacheInvalidated$ = new CacheInvalidationSubject<void>();
  private textCacheGeneration = 0;

  private invalidateTextCache(): void {
    this.textCacheGeneration++;
    this.textCacheInvalidated$.next();
  }

  private authInterceptor = inject(AuthInterceptor);
  private grpcWrapper = inject(GrpcWrapperService);

  private static instanceCounter = 0;
  private instanceId: number;

  selectedPage: ArchitecturalPlanPage | null = null;
  private pdfCache = new Map<string, Uint8Array>();
  private inFlightPdfRequests = new Map<string, Promise<Uint8Array>>();
  private chaptersCache = new Map<string, Chapters>();
  private planCache = new Map<string, Promise<any>>();
  private applicableSectionsCache = new Map<string, Promise<any>>();
  private complianceReportCache = new Map<string, Promise<any>>();
  private client: ArchitecturalPlanServiceClient;
  private reviewClient: ArchitecturalPlanReviewServiceClient;
  private analysisClient: ArchitecturalPlanAnalysisServiceClient;
  private bookInfoClient: IccBookInfoServiceClient;
  private codeApplicabilityClient: CodeApplicabilityServiceClient;
  private crossRefClient: CrossReferenceServiceClient;

  /**
   * Detects if a project uses legacy file structure based on GCS filesystem paths.
   *
   * Issue #167: Multi-file support introduced hierarchical structure
   *
   * @param pages Array of ArchitecturalPlanPage from the project
   * @returns true if project uses legacy structure, false if modern multi-file structure
   *
   * **Detection Logic:**
   * - Checks the `page_folder_path` field (GCS storage path, NOT URL route)
   * - Legacy: `projects/{planId}/pages/{pageNum}/` (no `/files/` segment)
   * - Modern: `projects/{planId}/files/{fileId}/pages/{pageNum}/`
   *
   * **Note about URL routes:**
   * - URL routes may show `/files/1/` even for legacy projects (UI consistency)
   * - This is intentional and works fine - the URL is "faked" for legacy projects
   * - Backend API calls must NOT include fileId for legacy projects (empty string)
   *
   * **Example Usage:**
   * ```typescript
   * const plan = await this.planReviewer.getArchitecturalPlan(projectId);
   * const isLegacy = PlanReviewerService.isLegacyProject(plan.getPagesList());
   *
   * if (isLegacy) {
   *   // Don't send fileId to backend - use empty string
   *   request.setFileId('');
   * } else {
   *   // Modern project - send fileId
   *   request.setFileId('1');
   * }
   * ```
   */
  static isLegacyProject(pages: ArchitecturalPlanPage[]): boolean {
    if (!pages || pages.length === 0) {
      return false; // No pages - assume modern (safe default)
    }

    // Iterate through pages to find any path that indicates modern structure
    // This is more robust than checking just the first page, which might have missing metadata
    const hasModernStructure = pages.some(page => {
      const path = page.getPageFolderPath();
      return path && path.includes('/files/');
    });

    // If we found any page with /files/ segment, it's definitely a modern project
    if (hasModernStructure) {
      return false;
    }

    // If we found NO pages with /files/, it's likely legacy.
    // But we should ensure we actually saw some valid paths before declaring legacy.
    // If all paths were empty, we shouldn't default to legacy (which strips fileId).
    const hasAnyValidPath = pages.some(page => {
      const path = page.getPageFolderPath();
      return path && path.length > 0;
    });

    if (!hasAnyValidPath) {
      // No valid paths found at all - unsafe to assume legacy
      // Default to modern (false) to preserve fileIds in API calls
      return false;
    }

    // We have valid paths and none had /files/, so it is legacy
    return true;
  }

  // Phase 3: Dynamic book availability tracking per project
  private availableBooksCache = new Map<string, CodeBook[]>();

  constructor() {
    this.instanceId = ++PlanReviewerService.instanceCounter;

    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Creating service instance`);

    const apiServer = this.environmentService.getApiServer();
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] apiServer:`, apiServer);
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] authInterceptor:`, this.authInterceptor);

    const options = {
      unaryInterceptors: [this.authInterceptor],
    };
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] options:`, options);

    this.client = new ArchitecturalPlanServiceClient(`https://${apiServer}:443`, null, options);
    this.reviewClient = new ArchitecturalPlanReviewServiceClient(`https://${apiServer}:443`, null, options);
    this.analysisClient = new ArchitecturalPlanAnalysisServiceClient(`https://${apiServer}:443`, null, options);
    this.bookInfoClient = new IccBookInfoServiceClient(`https://${apiServer}:443`, null, options);
    this.codeApplicabilityClient = new CodeApplicabilityServiceClient(`https://${apiServer}:443`, null, options);
    this.crossRefClient = new CrossReferenceServiceClient(`https://${apiServer}:443`, null, options);

    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] client created:`, this.client);
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] reviewClient created:`, this.reviewClient);
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] analysisClient created:`, this.analysisClient);
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] bookInfoClient created:`, this.bookInfoClient);
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] codeApplicabilityClient created:`,
      this.codeApplicabilityClient
    );
  }

  private getServiceLabel(): string {
    return PlanReviewerService.instanceCounter > 1 ? `PlanReviewerService #${this.instanceId}` : 'PlanReviewerService';
  }

  /**
   * Detects if a response contains a gRPC error (ESPv2 returns HTTP 200 with gRPC error in JSON)
   */
  private isGrpcErrorResponse(error: any): boolean {
    // Check if error has gRPC status code structure
    if (error && typeof error.code === 'number' && error.message) {
      return true;
    }

    // Check if it's wrapped in an error object
    if (error && error.error && typeof error.error.code === 'number' && error.error.message) {
      return true;
    }

    return false;
  }

  /**
   * Extracts gRPC error details from ESPv2 response
   */
  private extractGrpcError(error: any): {
    code: number;
    message: string;
    isPermissionDenied: boolean;
    isNotFound: boolean;
  } {
    let grpcError: GrpcErrorResponse;

    if (error.error && error.error.code) {
      grpcError = error.error;
    } else if (error.code) {
      grpcError = error;
    } else {
      return { code: 0, message: 'Unknown error', isPermissionDenied: false, isNotFound: false };
    }

    return {
      code: grpcError.code,
      message: grpcError.message || 'Unknown error',
      isPermissionDenied: grpcError.code === 7, // PERMISSION_DENIED
      isNotFound: grpcError.code === 5, // NOT_FOUND
    };
  }

  /**
   * Categorizes errors for better user experience and determines appropriate fallback behavior.
   * Phase 3 enhancement for graceful error handling.
   */
  private categorizeComplianceError(error: any, context: string): ComplianceErrorCategory {
    ClientLoggingService.log('DEBUG', `[PlanReviewerService] Categorizing compliance error in context: ${context}`);
    if (this.isGrpcErrorResponse(error)) {
      const grpcError = this.extractGrpcError(error);

      // Compliance analysis not available (the main issue we're solving)
      if (
        grpcError.code === 9 &&
        (grpcError.message.includes('page-only mode') ||
          grpcError.message.includes('not initialized') ||
          grpcError.message.includes('not available for this project'))
      ) {
        return {
          type: 'ANALYSIS_NOT_AVAILABLE',
          severity: 'info',
          userMessage:
            'Code compliance analysis is not available for this project. You may need to initiate compliance analysis first.',
          technicalMessage: grpcError.message,
          canRetry: false,
          showFallbackUI: true,
        };
      }

      // Backend initialization issues (our recent fixes should resolve these)
      if (
        grpcError.code === 13 &&
        (grpcError.message.includes('CodeApplicabilityInspector is required') ||
          grpcError.message.includes('CodeComplianceChecker') ||
          grpcError.message.includes('not initialized'))
      ) {
        return {
          type: 'BACKEND_INITIALIZATION',
          severity: 'warning',
          userMessage: 'Code compliance system is initializing. Please try again in a moment.',
          technicalMessage: grpcError.message,
          canRetry: true,
          showFallbackUI: true,
        };
      }

      // Permission issues
      if (grpcError.code === 7) {
        return {
          type: 'PERMISSION_DENIED',
          severity: 'error',
          userMessage: "You don't have permission to access code compliance features for this project.",
          technicalMessage: grpcError.message,
          canRetry: false,
          showFallbackUI: true,
        };
      }

      // Resource not found
      if (grpcError.code === 5) {
        return {
          type: 'NOT_FOUND',
          severity: 'warning',
          userMessage: 'The requested code book or project data was not found.',
          technicalMessage: grpcError.message,
          canRetry: true,
          showFallbackUI: true,
        };
      }

      // Service unavailable / backend issues
      if (grpcError.code === 14 || grpcError.code === 8) {
        return {
          type: 'SERVICE_UNAVAILABLE',
          severity: 'warning',
          userMessage: 'Code compliance service is temporarily unavailable. Please try again later.',
          technicalMessage: grpcError.message,
          canRetry: true,
          showFallbackUI: true,
        };
      }

      // Generic gRPC error
      return {
        type: 'GRPC_ERROR',
        severity: 'error',
        userMessage: 'An error occurred while accessing code compliance features.',
        technicalMessage: grpcError.message,
        canRetry: true,
        showFallbackUI: true,
      };
    }

    // Network or other errors
    return {
      type: 'NETWORK_ERROR',
      severity: 'error',
      userMessage: 'Network error occurred. Please check your connection and try again.',
      technicalMessage: error.message || 'Unknown network error',
      canRetry: true,
      showFallbackUI: true,
    };
  }

  /**
   * Enhanced error handler that properly detects gRPC errors in HTTP 200 responses
   */
  private handleGrpcError(error: any, context: string): never {
    if (error instanceof RpcAuthenticationError) throw error;
    if (this.isGrpcErrorResponse(error)) {
      const grpcError = this.extractGrpcError(error);
      throw new GrpcServiceError(grpcError.message, grpcError.code);
    }

    // Handle non-gRPC errors
    ClientLoggingService.log('ERROR', `💥 [${this.getServiceLabel()}] Network/service error in ${context}:`, error);
    throw error;
  }

  /**
   * Get the list of supported code books
   */
  getSupportedCodeBooks(): CodeBook[] {
    return [...SUPPORTED_CODE_BOOKS];
  }

  /**
   * Get available code books for a specific project, with dynamic discovery.
   * Phase 3: Replaces hardcoded books with project-specific availability detection.
   */
  async getAvailableCodeBooks(projectId?: string, pageNumber?: number, fileId?: string): Promise<CodeBook[]> {
    // If no project ID provided, return cached global list or hardcoded fallback
    if (!projectId) {
      return SUPPORTED_CODE_BOOKS.filter(book => book.available);
    }

    // If no page number provided, we can't determine page-specific availability
    if (!pageNumber) {
      ClientLoggingService.log(
        'WARN',
        `[${this.getServiceLabel()}] No page number provided for project ${projectId}. Cannot determine page-specific book availability.`
      );
      return []; // Return empty list - don't guess with page 1
    }

    // Create cache key that includes page number and file ID since availability can be page/file-specific
    const cacheKey = fileId ? `${projectId}::${pageNumber}::${fileId}` : `${projectId}::${pageNumber}`;

    // Check cache for this project-page-file combination
    if (this.availableBooksCache.has(cacheKey)) {
      return this.availableBooksCache.get(cacheKey)!;
    }

    // Discover available books for this specific project and page using GetAvailableAnalysis RPC
    const availableBooks = await this.discoverBooksWithAvailableAnalysis(projectId, pageNumber, fileId);

    // Cache the result
    this.availableBooksCache.set(cacheKey, availableBooks);

    return availableBooks;
  }

  /**
   * Discover code books with available analysis data for a project using the GetAvailableAnalysis RPC.
   * Uses the new IccBookInfoService to get accurate book metadata including document_slug.
   */
  private async discoverBooksWithAvailableAnalysis(
    projectId: string,
    pageNumber: number,
    fileId?: string
  ): Promise<CodeBook[]> {
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Discovering code books with available analysis for project: ${projectId}, page: ${pageNumber}, fileId: ${fileId || 'none'}`
    );

    try {
      const request = new GetAvailableAnalysisRequest();
      request.setProjectId(projectId);
      request.setPageNumber(pageNumber);

      // Issue #167: Set file_id for multi-file support
      if (fileId) {
        request.setFileId(fileId);
        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] Setting file_id: ${fileId} in GetAvailableAnalysis request`
        );
      }

      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Calling GetAvailableAnalysis for project ${projectId}, page ${pageNumber}, fileId: ${fileId || 'none'}`
      );

      const response = await this.analysisClient.getAvailableAnalysis(request, null);

      const availableBooks: CodeBook[] = [];
      const bookAvailabilities = response.getAvailableBooksList();

      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] GetAvailableAnalysis response: ${bookAvailabilities.length} books checked in ${response.getProcessingTimeMs()}ms`
      );

      // Get book IDs that have analysis available
      const bookIds = bookAvailabilities
        .filter(book => book.getApplicabilityAvailable() || book.getComplianceAvailable())
        .map(book => book.getIccBookId());

      if (bookIds.length === 0) {
        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] No books with analysis data found for project ${projectId}. This project may not have undergone compliance analysis yet.`
        );
        return [];
      }

      // Use the new IccBookInfoService to get complete book information
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Fetching book info for ${bookIds.length} books with analysis data`
      );

      try {
        const bookInfoRequest = new GetMultipleIccBookInfoRequest();
        bookInfoRequest.setDocumentIdsList(bookIds);

        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] Calling GetMultipleIccBookInfo for book IDs: ${bookIds.join(', ')}`
        );

        const bookInfoResponse = await this.bookInfoClient.getMultipleIccBookInfo(bookInfoRequest, null);

        for (const bookInfo of bookInfoResponse.getBookInfosList()) {
          // FIX for Issue #588: Use the correct lookup ID based on book type
          // - Legacy books: document_id (e.g., "2217") is the correct lookup ID
          // - Code Connect books: document_slug (e.g., "IBC2018P6") is the correct lookup ID
          //
          // Detection: If document_id matches one of our original bookIds from GetAvailableAnalysis,
          // it's a Legacy book and we should use document_id. Otherwise, use document_slug.
          const docId = bookInfo.getDocumentId().toString();
          const bookId = bookIds.includes(docId) ? docId : bookInfo.getDocumentSlug() || docId;

          const codeBook: CodeBook = {
            id: bookId,
            document_slug: bookInfo.getDocumentSlug(),
            title: bookInfo.getTitle(),
            abbreviation: bookInfo.getBookAbbr() || bookInfo.getAbbreviation(),
            year: bookInfo.getYear()?.getYear()?.toString() || bookInfo.getBookYear() || '',
            available: true,
          };

          availableBooks.push(codeBook);

          ClientLoggingService.log(
            'INFO',
            `[${this.getServiceLabel()}] ✅ ${codeBook.abbreviation} book info retrieved ` +
              `(id: ${codeBook.id}, document_slug: ${codeBook.document_slug}, year: ${codeBook.year})`
          );
        }
      } catch (bookInfoError) {
        ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Failed to fetch book info:`, bookInfoError);

        // If IccBookInfoService fails, we can't get proper document_slug values
        // Return empty array to trigger fallback to hardcoded books
        ClientLoggingService.log(
          'WARN',
          `[${this.getServiceLabel()}] IccBookInfoService unavailable, cannot determine document_slug for books`
        );
        return [];
      }

      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Discovery complete: ${availableBooks.length} books available for project ${projectId}`
      );
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Available books: ${availableBooks.map(b => `${b.abbreviation}(${b.document_slug})`).join(', ')}`
      );

      return availableBooks;
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        `[${this.getServiceLabel()}] Failed to discover available books for project ${projectId}:`,
        error
      );

      // Fallback to hardcoded books if the service is unavailable
      ClientLoggingService.log('WARN', `[${this.getServiceLabel()}] Falling back to hardcoded book configuration`);
      return SUPPORTED_CODE_BOOKS.filter(book => book.available);
    }
  }

  /**
   * Get chapters for a specific ICC book
   */
  async getIccBookChapters(bookId: string): Promise<Array<{ contentId: string; title: string }>> {
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Fetching chapters for ICC book: ${bookId}`);

    try {
      const request = new GetIccBookTableOfContentsRequest();
      request.setIccBookId(bookId);

      const response = await this.reviewClient.getIccBookTableOfContents(request, null);
      const chapters = response.getChaptersList().map((chapter: any) => ({
        contentId: chapter.getContentId().toString(),
        title: chapter.getTitle(),
      }));

      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Retrieved ${chapters.length} chapters for book ${bookId}`
      );
      return chapters;
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        `[${this.getServiceLabel()}] Failed to fetch chapters for book ${bookId}:`,
        error
      );
      return [];
    }
  }

  /**
   * Start code applicability analysis with the collected wizard parameters
   * Issue #167: Updated to be file-aware for multi-file projects
   */
  async startCodeApplicabilityAnalysis(
    projectId: string,
    pageNumber: number,
    bookId: string,
    chapterSelectionMode: 'ai' | 'manual',
    maxChapters: number,
    selectedChapterIds: string[],
    maxSectionDepth: number,
    forceRecompute = false
  ): Promise<{ taskId: string; success: boolean; message: string }> {
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Starting code applicability analysis:`, {
      projectId,
      pageNumber,
      bookId,
      chapterSelectionMode,
      maxChapters,
      selectedChapterIds,
      maxSectionDepth,
      forceRecompute,
    });

    try {
      // Issue #167: Extract file ID from the currently selected page
      const fileId = this.extractFileIdFromActivePage();
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Extracted file ID for analysis: ${fileId || 'none (legacy)'}`
      );

      // Create the gRPC request
      const request = new StartCodeApplicabilityAnalysisRequest();
      request.setArchitecturalProjectId(projectId);
      request.setPageNumber(pageNumber);
      request.setIccBookId(bookId);

      // Issue #167: Include file ID for multi-file support
      if (fileId) {
        request.setFileId(fileId);
        ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] ✅ Set file ID in gRPC request: ${fileId}`);
      } else {
        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] ❌ No file ID to set in gRPC request (legacy mode)`
        );
      }

      // Set chapter selection parameters
      if (chapterSelectionMode === 'ai' && maxChapters > 0) {
        request.setMaxRelevantChapters(maxChapters);
        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] Using AI chapter selection: top ${maxChapters} chapters`
        );
      } else if (chapterSelectionMode === 'manual' && selectedChapterIds.length > 0) {
        request.setSpecificChapterIdsList(selectedChapterIds);
        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] Using manual chapter selection: ${selectedChapterIds.length} chapters`
        );
      } else {
        // No filtering - analyze all chapters
        ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Using comprehensive analysis: all chapters`);
      }

      // Set section depth parameters
      if (maxSectionDepth !== -1) {
        request.setMinSectionDepth(1); // Always start from main sections
        request.setMaxSectionDepth(maxSectionDepth);
        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] Using section depth filter: 1-${maxSectionDepth}`
        );
      } else {
        ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Using all section depths`);
      }

      // Issue #1426: Set force_recompute flag if requested
      if (forceRecompute) {
        request.setForceRecompute(true);
        ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Force recompute enabled (skipping cache)`);
      }

      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Calling StartCodeApplicabilityAnalysis with request:`,
        {
          architecturalProjectId: request.getArchitecturalProjectId(),
          pageNumber: request.getPageNumber(),
          iccBookId: request.getIccBookId(),
          fileId: request.getFileId(),
          maxRelevantChapters: request.getMaxRelevantChapters(),
          maxSectionDepth: request.getMaxSectionDepth(),
          forceRecompute: request.getForceRecompute(),
        }
      );

      // Make the actual gRPC call
      const response = await this.codeApplicabilityClient.startCodeApplicabilityAnalysisTask(request, null);

      return {
        taskId: response.getTaskId(),
        success: response.getSuccess(),
        message: response.getMessage(),
      };
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        `[${this.getServiceLabel()}] Failed to start code applicability analysis:`,
        error
      );
      const errorMsg = error instanceof Error ? error.message : String(error);
      const detail = extractUserFacingErrorDetail([errorMsg]);
      return {
        taskId: '',
        success: false,
        message: detail || `Failed to start analysis: ${errorMsg}`,
      };
    }
  }

  /**
   * Build the cross-reference graph for a project (Issue #1066).
   * Triggers async two-tier LLM analysis of all ingested pages.
   */
  async buildCrossReferenceGraph(projectId: string): Promise<{ taskId: string; success: boolean; message: string }> {
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Building cross-reference graph for project: ${projectId}`
    );

    try {
      const request = new BuildCrossReferenceGraphRequest();
      request.setProjectId(projectId);

      const response: BuildCrossReferenceGraphResponse = await this.crossRefClient.buildCrossReferenceGraph(
        request,
        null
      );

      return {
        taskId: response.getTaskId(),
        success: response.getSuccess(),
        message: response.getMessage(),
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Failed to build cross-reference graph:`, error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      return {
        taskId: '',
        success: false,
        message: `Failed to build cross-reference graph: ${errorMsg}`,
      };
    }
  }

  /**
   * Fetch the cross-reference graph for a project (Issue #1106).
   * Returns the full graph with nodes, edges, and metadata.
   */
  async getCrossReferenceGraph(projectId: string): Promise<GetCrossReferenceGraphResponse | null> {
    try {
      const request = new GetCrossReferenceGraphRequest();
      request.setProjectId(projectId);
      return await this.crossRefClient.getCrossReferenceGraph(request, null);
    } catch (error) {
      ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Failed to get cross-reference graph:`, error);
      return null;
    }
  }

  /**
   * Triggers building element registry detection for a project. Issue: #1119
   */
  async buildElementRegistry(projectId: string): Promise<{ taskId: string; success: boolean; message: string }> {
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Building element registry for project: ${projectId}`);
    try {
      const request = new BuildElementRegistryRequest();
      request.setProjectId(projectId);
      const response: BuildElementRegistryResponse = await this.crossRefClient.buildElementRegistry(request, null);
      return {
        taskId: response.getTaskId(),
        success: response.getSuccess(),
        message: response.getMessage(),
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Failed to build element registry:`, error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      return {
        taskId: '',
        success: false,
        message: `Failed to build element registry: ${errorMsg}`,
      };
    }
  }

  /**
   * Retrieves the building element registry for a project. Issue: #1119
   */
  async getElementRegistry(projectId: string): Promise<GetElementRegistryResponse | null> {
    try {
      const request = new GetElementRegistryRequest();
      request.setProjectId(projectId);
      return await this.crossRefClient.getElementRegistry(request, null);
    } catch (error) {
      ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Failed to get element registry:`, error);
      return null;
    }
  }

  /**
   * Triggers cross-examination for a project. Issue: #1103
   */
  async runCrossExamination(projectId: string): Promise<{ taskId: string; success: boolean; message: string }> {
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Running cross-examination for project: ${projectId}`);
    try {
      const request = new RunCrossExaminationRequest();
      request.setProjectId(projectId);
      const response: RunCrossExaminationResponse = await this.crossRefClient.runCrossExamination(request, null);
      return {
        taskId: response.getTaskId(),
        success: response.getSuccess(),
        message: response.getMessage(),
      };
    } catch (error) {
      ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Failed to run cross-examination:`, error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      return {
        taskId: '',
        success: false,
        message: `Failed to run cross-examination: ${errorMsg}`,
      };
    }
  }

  /**
   * Retrieves cross-examination findings for a project. Issue: #1103
   */
  async getCrossExamination(projectId: string): Promise<GetCrossExaminationResponse | null> {
    try {
      const request = new GetCrossExaminationRequest();
      request.setProjectId(projectId);
      return await this.crossRefClient.getCrossExamination(request, null);
    } catch (error) {
      ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Failed to get cross-examination:`, error);
      return null;
    }
  }

  /**
   * Check if there's an active code applicability task for a specific page
   */
  hasActiveCodeApplicabilityTask(projectId: string, pageNumber: number): boolean {
    // TODO: This would need access to TaskCounterService to check for active tasks
    // For now, return false - this will be implemented when we integrate with TaskCounterService
    ClientLoggingService.log(
      'DEBUG',
      `[PlanReviewerService] hasActiveCodeApplicabilityTask stub called for project ${projectId}, page ${pageNumber}`
    );
    return false;
  }

  /**
   * Get all available ICC books from the backend
   */
  async getAllAvailableBooks(): Promise<Array<{ id: string; title: string; abbreviation: string; year: string }>> {
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Fetching all available ICC books`);

    try {
      // Step 1: Get list of available book IDs
      const availableRequest = new GetAvailableBookInfoRequest();
      const availableResponse = await this.bookInfoClient.getAvailableBookInfo(availableRequest, null);

      const bookIds = availableResponse.getAvailableDocumentIdsList();
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Found ${bookIds.length} available book IDs: ${bookIds.join(', ')}`
      );

      if (bookIds.length === 0) {
        ClientLoggingService.log('WARN', `[${this.getServiceLabel()}] No books available from GetAvailableBookInfo`);
        return [];
      }

      // Step 2: Get detailed info for all available books
      const bookInfoRequest = new GetMultipleIccBookInfoRequest();
      bookInfoRequest.setDocumentIdsList(bookIds);

      const bookInfoResponse = await this.bookInfoClient.getMultipleIccBookInfo(bookInfoRequest, null);

      // Step 3: Transform to simple format for UI
      // FIX for Issue #588: Use the correct lookup ID based on book type
      // - Legacy books: document_id (e.g., "2217") is the correct lookup ID
      // - Code Connect books: document_slug (e.g., "IBC2018P6") is the correct lookup ID
      const books = bookInfoResponse.getBookInfosList().map(bookInfo => {
        const docId = bookInfo.getDocumentId().toString();
        const bookId = bookIds.includes(docId) ? docId : bookInfo.getDocumentSlug() || docId;
        return {
          id: bookId,
          title: bookInfo.getTitle(),
          abbreviation: bookInfo.getBookAbbr() || bookInfo.getAbbreviation() || bookInfo.getTitle(),
          year: bookInfo.getYear()?.getYear()?.toString() || bookInfo.getBookYear() || '',
        };
      });

      ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Retrieved ${books.length} book details`);
      return books;
    } catch (error) {
      ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Failed to fetch available books:`, error);
      return [];
    }
  }

  /**
   * Check if a specific code book is available
   */
  isCodeBookAvailable(codeBookId: string): boolean {
    const codeBook = SUPPORTED_CODE_BOOKS.find(book => book.id === codeBookId);
    return codeBook ? codeBook.available : false;
  }

  /**
   * Get the default code book (for backward compatibility)
   */
  getDefaultCodeBook(): CodeBook {
    return DEFAULT_CODE_BOOK;
  }

  /**
   * Find a code book by its document_slug
   */
  getCodeBookByDocumentSlug(documentSlug: string): CodeBook | null {
    return SUPPORTED_CODE_BOOKS.find(book => book.document_slug === documentSlug) || null;
  }

  /**
   * Extract document_slug from a section ID and find the corresponding CodeBook
   * Section IDs typically start with the document_slug followed by underscore (e.g., "IBC2021P2_Ch01_Sec101")
   */
  getCodeBookFromSectionId(sectionId: string): CodeBook {
    if (!sectionId) {
      return DEFAULT_CODE_BOOK;
    }

    // Extract the document_slug prefix from section ID
    const slugMatch = sectionId.match(/^([A-Z]+\d{4}P\d+)_/);
    if (slugMatch) {
      const documentSlug = slugMatch[1];
      const codeBook = this.getCodeBookByDocumentSlug(documentSlug);
      if (codeBook) {
        return codeBook;
      }
    }

    // Fallback to default if no match found
    return DEFAULT_CODE_BOOK;
  }

  /**
   * Get the architectural plan by ID.
   * @param planId The ID of the architectural plan.
   * @param noCache Bypasses the cache for a fresh fetch.
   */
  getArchitecturalPlan(planId: string, noCache = false): Promise<any> {
    if (!noCache && this.planCache.has(planId)) {
      ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Returning cached architectural plan for ${planId}`);
      return this.planCache.get(planId)!;
    }

    if (noCache) {
      this.invalidateTextCache();
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Cache bypassed for architectural plan for ${planId}`
      );
    }

    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Fetching architectural plan with ID: ${planId}`);
    // The host only: the client object reaches much of the app and is slow to log (Issue #2983).
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] getArchitecturalPlan - using host: ${this.environmentService.getApiServer()}`
    );

    const request = new GetArchitecturalPlanRequest();
    request.setArchitecturalPlanId(planId);

    const generation = this.textCacheGeneration;
    const planPromise = this.client
      .getArchitecturalPlan(request, null)
      .then(response => {
        // Only cache successful responses
        if (generation === this.textCacheGeneration) this.planCache.set(planId, Promise.resolve(response));
        return response;
      })
      .catch(error => {
        // Don't cache failed requests - remove from cache if it exists
        if (generation === this.textCacheGeneration) this.planCache.delete(planId);
        // Use the new handler to throw a more specific error, which will be caught by the component
        this.handleGrpcError(error, `getArchitecturalPlan for planId: ${planId}`);
      });

    return planPromise;
  }

  /** Reads the persisted file Overview projection. This never starts model-backed work. */
  async getFileOverviewSummary(projectId: string, fileId: string): Promise<FileOverviewSummary> {
    const request = new GetFileOverviewSummaryRequest();
    request.setArchitecturalPlanId(projectId);
    request.setFileId(fileId);
    try {
      return await this.reviewClient.getFileOverviewSummary(request, null);
    } catch (error) {
      this.handleGrpcError(error, `getFileOverviewSummary for ${projectId}/${fileId}`);
    }
  }

  /** Reads the current-safe aggregate compliance projection for one file. */
  async getFileComplianceSummary(projectId: string, fileId: string): Promise<FileComplianceSummary> {
    const request = new GetFileComplianceSummaryRequest();
    request.setArchitecturalPlanId(projectId);
    request.setFileId(fileId);
    try {
      return await this.reviewClient.getFileComplianceSummary(request, null);
    } catch (error) {
      this.handleGrpcError(error, `getFileComplianceSummary for ${projectId}/${fileId}`);
    }
  }

  /** Loads one bounded drill-down page for an exact file, section, and risk/coverage filter. */
  async listFileCompliancePages(options: {
    projectId: string;
    fileId: string;
    bookId: string;
    sectionId: string;
    sourceVersion: string;
    risk?: ComplianceRiskBucket;
    coverageState?: FileComplianceCoverageState;
    pageSize?: number;
    pageToken?: string;
  }): Promise<ListFileCompliancePagesResponse> {
    const request = new ListFileCompliancePagesRequest();
    request.setArchitecturalPlanId(options.projectId);
    request.setFileId(options.fileId);
    request.setIccBookId(options.bookId);
    request.setCanonicalSectionId(options.sectionId);
    request.setSourceVersion(options.sourceVersion);
    request.setPageSize(options.pageSize ?? 50);
    if (options.pageToken) {
      request.setPageToken(options.pageToken);
    }
    if (options.risk !== undefined) {
      request.setRisk(options.risk);
    } else if (options.coverageState !== undefined) {
      request.setCoverageState(options.coverageState);
    } else {
      throw new Error('A risk or coverage-state filter is required');
    }
    try {
      return await this.reviewClient.listFileCompliancePages(request, null);
    } catch (error) {
      this.handleGrpcError(
        error,
        `listFileCompliancePages for ${options.projectId}/${options.fileId}/${options.sectionId}`
      );
    }
  }

  setSelectedPage(page: ArchitecturalPlanPage): void {
    this.selectedPage = page;
  }

  /**
   * Clears all caches related to plan data.
   * Useful when switching between different plans.
   */
  clearPlanDataCache(): void {
    this.invalidateTextCache();
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Clearing all plan data caches`);
    this.planCache.clear();
    this.applicableSectionsCache.clear();
    this.complianceReportCache.clear();
    this.pdfCache.clear(); // Issue #167: Clear PDF cache to prevent file mix-ups
    this.inFlightPdfRequests.clear();
  }

  /**
   * Clears the caches for a specific page.
   * @param page The page whose compliance report cache should be cleared
   */
  clearPageDataCache(page: ArchitecturalPlanPage): void {
    this.invalidateTextCache();
    // Issue #167: Clear compliance report cache with file-aware keys
    const pageFolderPath = page.getPageFolderPath();
    const fileIdMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
    const fileId = fileIdMatch ? fileIdMatch[1] : 'legacy';
    const complianceCacheKey = `${page.getArchitecturalPlanId()}-${fileId}-${page.getPageNumber()}`;
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Clearing compliance report cache for ${complianceCacheKey}*`
    );

    // Clear all cache entries that start with this project-file-page combination
    const keysToDelete = Array.from(this.complianceReportCache.keys()).filter(key =>
      key.startsWith(complianceCacheKey)
    );
    keysToDelete.forEach(key => this.complianceReportCache.delete(key));

    const applicableSectionsCacheKey = `${page.getArchitecturalPlanId()}-${fileId}-${page.getPageNumber()}`;
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Clearing applicable sections cache for ${applicableSectionsCacheKey}`
    );
    this.applicableSectionsCache.delete(applicableSectionsCacheKey);
  }

  /**
   * Clears PDF cache to prevent wrong pages from being displayed in multi-file projects.
   * Issue #167: Called when switching between files to ensure correct PDF is loaded.
   */
  clearPdfCache(): void {
    this.invalidateTextCache();
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Clearing PDF cache to prevent file mix-ups`);
    this.pdfCache.clear();
    this.inFlightPdfRequests.clear();
  }

  /** Evicts a single page from the L1 PDF byte cache so the next fetch retrieves fresh data from the backend. */
  invalidatePagePdfCache(page: ArchitecturalPlanPage): void {
    this.invalidateTextCache();
    const pageFolder = page.getPageFolderPath() || '';
    const baseKey = pageFolder ? `path-${pageFolder}` : page.getPageId() || `page-${page.getPageNumber()}`;
    const cacheKey = `${page.getArchitecturalPlanId()}:${baseKey}`;
    this.inFlightPdfRequests.delete(cacheKey);
    if (this.pdfCache.has(cacheKey)) {
      this.pdfCache.delete(cacheKey);
      ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] L1 PDF cache invalidated for ${cacheKey}`);
    }
  }

  /**
   * Extracts file ID from a given page's pageFolderPath.
   * Issue #167: Used for file-aware operations like compliance analysis.
   * Issue #484: Fixed to use page parameter instead of this.selectedPage.
   *
   * @param page The page to extract file ID from
   * @returns The file ID string (e.g., "1") or null for legacy projects
   */
  private extractFileIdFromPage(page: ArchitecturalPlanPage): string | null {
    const pageFolderPath = page.getPageFolderPath() || '';

    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Extracting file ID from page ${page.getPageNumber()}, path: ${pageFolderPath}`
    );

    // Extract file ID from modern structure path: projects/{projectId}/files/{fileId}/pages/{pageNum}/
    const fileIdMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] ✅ Extracted file ID: ${fileId} from page ${page.getPageNumber()}`
      );
      return fileId;
    }

    // For legacy structure, no file ID available
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] ❌ Legacy structure for page ${page.getPageNumber()}, no file ID`
    );
    return null;
  }

  /**
   * Extracts file ID from the currently selected page's pageFolderPath.
   * Issue #167: Used for file-aware operations like code applicability analysis.
   *
   * @deprecated Use extractFileIdFromPage(page) instead when a page parameter is available.
   *             This method relies on this.selectedPage which may be stale.
   */
  private extractFileIdFromActivePage(): string | null {
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Attempting to extract file ID from selected page`);
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Selected page:`,
      this.selectedPage
        ? {
            pageNumber: this.selectedPage.getPageNumber(),
            title: this.selectedPage.getTitle(),
            pageId: this.selectedPage.getPageId(),
            pageFolderPath: this.selectedPage.getPageFolderPath(),
          }
        : 'null'
    );

    if (!this.selectedPage) {
      ClientLoggingService.log('WARN', `[${this.getServiceLabel()}] No selected page available for file ID extraction`);
      return null;
    }

    return this.extractFileIdFromPage(this.selectedPage);
  }

  /**
   * Fetches a single page with full content (including pageMarkdown and explanationMarkdown).
   * This triggers backend self-healing if markdown fields are missing from metadata.
   *
   * @param planId The architectural plan ID
   * @param pageNumber The page number
   * @param fileId Optional file ID for multi-file projects
   * @returns Promise resolving to the full page with all fields populated
   */
  getPageWithFullContent(planId: string, pageNumber: number, fileId?: string): Promise<ArchitecturalPlanPage> {
    return new Promise((resolve, reject) => {
      const request = new GetArchitecturalPlanPageRequest();
      request.setArchitecturalPlanId(planId);
      request.setPageNumber(pageNumber);

      if (fileId) {
        request.setFileId(fileId);
      }

      ClientLoggingService.log(
        'INFO',
        `[PlanReviewerService] Fetching full page content: planId=${planId}, pageNumber=${pageNumber}, fileId=${fileId || 'legacy'}`
      );

      this.client.getArchitecturalPlanPage(request, null).then(
        (page: ArchitecturalPlanPage) => {
          ClientLoggingService.log(
            'INFO',
            `[PlanReviewerService] Received full page content, pageMarkdown length: ${page.getPageMarkdown().length}`
          );
          resolve(page);
        },
        (error: any) => {
          ClientLoggingService.log('ERROR', '[PlanReviewerService] Error fetching full page content:', error);
          try {
            this.handleGrpcError(error, `getPageWithFullContent for planId: ${planId}, page: ${pageNumber}`);
          } catch (processedError) {
            reject(processedError);
          }
        }
      );
    });
  }

  getPagePdf(page: ArchitecturalPlanPage): Promise<Uint8Array> {
    const generation = this.textCacheGeneration;
    // Issue #167: Use pageFolderPath in cache key to distinguish pages from different files.
    const pageFolder = page.getPageFolderPath() || '';
    const baseKey = pageFolder ? `path-${pageFolder}` : page.getPageId() || `page-${page.getPageNumber()}`;
    const cacheKey = `${page.getArchitecturalPlanId()}:${baseKey}`;

    ClientLoggingService.log(
      'INFO',
      `[PlanReviewerService] getPagePdf for page ${page.getPageNumber()}, cacheKey: ${cacheKey}`
    );
    ClientLoggingService.log('INFO', `[PlanReviewerService] Page details:`, {
      pageNumber: page.getPageNumber(),
      title: page.getTitle(),
      pageId: page.getPageId(),
      pageFolderPath: pageFolder,
      architecturalPlanId: page.getArchitecturalPlanId(),
    });

    const cached = this.pdfCache.get(cacheKey);
    if (cached) {
      ClientLoggingService.log('INFO', `[PlanReviewerService] PDF cache hit for ${cacheKey}`);
      return Promise.resolve(cached);
    }

    const inFlight = this.inFlightPdfRequests.get(cacheKey);
    if (inFlight) {
      ClientLoggingService.log('INFO', `[PlanReviewerService] PDF in-flight cache hit for ${cacheKey}`);
      return inFlight;
    }

    ClientLoggingService.log('INFO', `[PlanReviewerService] PDF cache miss for ${cacheKey}, making gRPC request`);

    const request = new GetArchitecturalPlanPageRequest();
    request.setArchitecturalPlanId(page.getArchitecturalPlanId());
    request.setPageNumber(page.getPageNumber());

    // Issue #167: Extract file ID from pageFolderPath for multi-file support.
    const fileIdMatch = pageFolder.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      request.setFileId(fileId);
      ClientLoggingService.log(
        'INFO',
        `[PlanReviewerService] gRPC request: planId=${page.getArchitecturalPlanId()}, pageNumber=${page.getPageNumber()}, fileId=${fileId}`
      );
    } else {
      ClientLoggingService.log(
        'INFO',
        `[PlanReviewerService] gRPC request: planId=${page.getArchitecturalPlanId()}, pageNumber=${page.getPageNumber()} (legacy - no fileId)`
      );
    }

    const loading = this.client
      .getArchitecturalPlanPagePdf(request, null)
      .then(response => {
        const pdfContent = response.getPdfContent_asU8();
        ClientLoggingService.log(
          'INFO',
          `[PlanReviewerService] Received PDF content for ${cacheKey}, size: ${pdfContent.length} bytes`
        );
        if (generation === this.textCacheGeneration) this.pdfCache.set(cacheKey, pdfContent);
        return pdfContent;
      })
      .catch(error => {
        ClientLoggingService.log('ERROR', `[PlanReviewerService] Error getting PDF for ${cacheKey}:`, error);
        this.handleGrpcError(error, `getPagePdf for page ${page.getPageNumber()}`);
      });
    this.inFlightPdfRequests.set(cacheKey, loading);
    void loading.then(
      () => this.finishPdfRequest(cacheKey, loading),
      () => this.finishPdfRequest(cacheKey, loading)
    );
    return loading;
  }

  private finishPdfRequest(cacheKey: string, loading: Promise<Uint8Array>): void {
    if (this.inFlightPdfRequests.get(cacheKey) === loading) this.inFlightPdfRequests.delete(cacheKey);
  }

  /** Fetches the region hierarchy tree for a page (Issue #1228). */
  async getRegionHierarchy(planId: string, pageNumber: number, fileId?: string): Promise<GetRegionHierarchyResponse> {
    const request = new GetRegionHierarchyRequest();
    request.setArchitecturalPlanId(planId);
    request.setPageNumber(pageNumber);
    if (fileId) {
      request.setFileId(fileId);
    }

    ClientLoggingService.log(
      'INFO',
      `[PlanReviewerService] getRegionHierarchy: planId=${planId}, page=${pageNumber}, fileId=${fileId ?? ''}`
    );

    return this.client.getRegionHierarchy(request, null);
  }

  private async getTableOfContents(codeBook?: CodeBook): Promise<Chapters> {
    const targetCodeBook = codeBook || this.getDefaultCodeBook();
    const cacheKey = `toc-${targetCodeBook.id}`;

    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] getTableOfContents - using reviewClient:`,
      this.reviewClient
    );

    if (this.chaptersCache.has(cacheKey)) {
      return this.chaptersCache.get(cacheKey)!;
    }

    const request = new GetIccBookTableOfContentsRequest();
    request.setIccBookId(targetCodeBook.id);

    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] getTableOfContents - calling reviewClient.getIccBookTableOfContents with Promise API`
    );

    try {
      // Use Promise-based API instead of callback-based to trigger AuthInterceptor
      const response = await this.reviewClient.getIccBookTableOfContents(request, null);
      this.chaptersCache.set(cacheKey, response);
      return response;
    } catch (error) {
      this.handleGrpcError(error, `getTableOfContents for ${targetCodeBook.abbreviation}`);
    }
  }

  /**
   * Section titles for one book, keyed by section number, walked to every depth (Issue #1773).
   *
   * The inline code links in a compliance report name subsections like `905.3.1` and `905.5.1`,
   * which sit BELOW the rows the compliance tree draws — that tree is filtered to applicable
   * sections, so building a tooltip index from it left the deepest references, the ones most worth
   * explaining, with no title at all. A report opened by deep link had none. The fallback fired
   * always, which looks like working software rather than like a bug.
   *
   * This walks the book's own table of contents instead. It is the same data the page already
   * requested and `chaptersCache` already holds, so it costs nothing extra on a warm cache and one
   * request on a cold one — the "no network call per link" constraint is met by the cache, not by
   * choosing a smaller source.
   */
  async getCodeSectionTitleIndex(codeBook?: CodeBook): Promise<ReadonlyMap<string, string>> {
    const index = new Map<string, string>();
    const chapters = await this.getTableOfContents(codeBook);
    if (!chapters?.getChaptersList) return index;

    const record = (id: string, title: string) => {
      const match = /_Sec([0-9A-Za-z][0-9A-Za-z.]*)$/.exec(id ?? '');
      if (!match) return;
      const cleaned = (title ?? '').trim();
      if (!cleaned) return;
      if (!index.has(match[1])) index.set(match[1], cleaned);
    };

    const walk = (subSections: SubSection[] | undefined) => {
      for (const subSection of subSections ?? []) {
        const link = subSection.getLink?.();
        if (link) record(link.getId(), link.getTitle());
        if (subSection.getSubSectionsList) walk(subSection.getSubSectionsList());
      }
    };

    chapters.getChaptersList().forEach((chapter: Chapter) => {
      if (chapter.getSubSectionsList) walk(chapter.getSubSectionsList());
    });
    return index;
  }

  async getApplicableSections(page: ArchitecturalPlanPage): Promise<any> {
    // Issue #484: Use page parameter directly instead of this.selectedPage
    const activeFileId = this.extractFileIdFromPage(page);
    const cacheKey = activeFileId
      ? `${page.getArchitecturalPlanId()}-${activeFileId}-${page.getPageNumber()}`
      : `${page.getArchitecturalPlanId()}-${page.getPageNumber()}`;
    if (this.applicableSectionsCache.has(cacheKey)) {
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Returning cached applicable sections for ${cacheKey}`
      );
      return this.applicableSectionsCache.get(cacheKey)!;
    }

    // Phase 3: Dynamically discover available code books for this specific project and page
    const availableBooks = await this.getAvailableCodeBooks(
      page.getArchitecturalPlanId(),
      page.getPageNumber(),
      activeFileId || undefined
    );
    if (availableBooks.length === 0) {
      ClientLoggingService.log(
        'WARN',
        `[${this.getServiceLabel()}] No available code books for applicable sections for project ${page.getArchitecturalPlanId()}`
      );
      return Promise.resolve({ getPageApplicabilityAnalysisList: () => [] });
    }

    // Fetch applicable sections for all available code books
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Fetching applicable sections for ${cacheKey} using ${availableBooks.length} dynamically discovered code books`
    );

    // NEW (Issue #167): Use file_id extracted from URL path instead of looking it up
    const fileId = activeFileId;
    if (fileId) {
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Using file_id=${fileId} for page ${page.getPageNumber()} (modern structure)`
      );
    } else {
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] No file_id for page ${page.getPageNumber()} (legacy mode)`
      );
    }

    const promises = availableBooks.map(codeBook => {
      const request = new GetApplicableCodeSectionsRequest();
      request.setArchitecturalPlanId(page.getArchitecturalPlanId());
      request.setPageNumber(page.getPageNumber());
      request.setIccBookId(codeBook.id);

      // NEW (Issue #167): Set file_id if available (modern structure optimization)
      if (fileId) {
        request.setFileId(fileId);
      }

      return new Promise<any>((resolve, reject) => {
        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] getApplicableSections - calling reviewClient.getApplicableCodeSections for:`,
          codeBook.abbreviation
        );
        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] getApplicableSections - reviewClient instance:`,
          this.reviewClient
        );

        // Use Promise-based API instead of callback-based to trigger AuthInterceptor
        this.reviewClient
          .getApplicableCodeSections(request, null)
          .then(response => {
            resolve({ response, codeBook });
          })
          .catch(err => {
            // Phase 3: Enhanced error handling with categorization
            const errorCategory = this.categorizeComplianceError(
              err,
              `getApplicableCodeSections for ${codeBook.abbreviation}`
            );

            // Log with appropriate severity
            if (errorCategory.severity === 'error') {
              ClientLoggingService.log(
                'ERROR',
                `[${this.getServiceLabel()}] ${errorCategory.type} error in getApplicableCodeSections for ${codeBook.abbreviation}: ${errorCategory.technicalMessage}`
              );
            } else if (errorCategory.severity === 'warning') {
              ClientLoggingService.log(
                'WARN',
                `[${this.getServiceLabel()}] ${errorCategory.type} warning in getApplicableCodeSections for ${codeBook.abbreviation}: ${errorCategory.technicalMessage}`
              );
            } else {
              ClientLoggingService.log(
                'INFO',
                `[${this.getServiceLabel()}] ${errorCategory.type} info in getApplicableCodeSections for ${codeBook.abbreviation}: ${errorCategory.technicalMessage}`
              );
            }

            // A failed saved-applicability read is not an intentional empty result.
            // Reject so the component presents its recoverable error state.
            reject(err);
          });
      });
    });

    const promise = Promise.all(promises).then(results => {
      // Combine all results into a single response
      const combinedAnalysisList: any[] = [];
      results.forEach(({ response, codeBook }) => {
        if (response && response.getPageApplicabilityAnalysisList) {
          const analysisList = response.getPageApplicabilityAnalysisList();
          ClientLoggingService.log(
            'DEBUG',
            `[PlanReviewerService] Processing ${analysisList.length} analyses from ${codeBook.abbreviation}`
          );
          analysisList.forEach((analysis: any) => {
            combinedAnalysisList.push(analysis);
          });
        }
      });

      return {
        getPageApplicabilityAnalysisList: () => combinedAnalysisList,
      };
    });

    this.applicableSectionsCache.set(cacheKey, promise);
    promise.catch(() => {
      if (this.applicableSectionsCache.get(cacheKey) === promise) {
        this.applicableSectionsCache.delete(cacheKey);
      }
    });
    return promise;
  }

  async getPageComplianceReport(page: ArchitecturalPlanPage): Promise<any> {
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] getPageComplianceReport called for page:`,
      page.getPageNumber()
    );

    // Issue #484: Use page parameter directly instead of this.selectedPage
    const activeFileId = this.extractFileIdFromPage(page);

    const availableBooks = await this.getAvailableCodeBooks(
      page.getArchitecturalPlanId(),
      page.getPageNumber(),
      activeFileId || undefined
    );
    if (availableBooks.length === 0) {
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] No books with analysis data found for project ${page.getArchitecturalPlanId()}. This project may not have undergone compliance analysis yet.`
      );

      // Return error information for the component to handle
      return Promise.resolve({
        sections: [],
        getReportSummary: () => 'No code books available for this project.',
        errorCategories: [
          {
            type: 'ANALYSIS_NOT_AVAILABLE',
            severity: 'info',
            userMessage:
              'Code compliance analysis is not available for this project. You may need to initiate compliance analysis first.',
            technicalMessage: 'No ICC books with analysis data found via GetAvailableAnalysis service',
            canRetry: false,
            showFallbackUI: true,
          },
        ],
      });
    }

    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Using ${availableBooks.length} dynamically discovered books for compliance report: ${availableBooks.map(b => b.abbreviation).join(', ')}`
    );

    // Create a cache key that includes all available books and file ID (Issue #167)
    const bookIds = availableBooks
      .map(book => book.id)
      .sort()
      .join('-');
    const pageFolderPath = page.getPageFolderPath() || '';
    const fileIdMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
    const fileId = fileIdMatch ? fileIdMatch[1] : 'legacy';
    const cacheKey = `${page.getArchitecturalPlanId()}-${fileId}-${page.getPageNumber()}-${bookIds}`;

    // Check if we already have a promise for this request
    if (this.complianceReportCache.has(cacheKey)) {
      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Returning cached compliance report for ${cacheKey}`
      );
      return this.complianceReportCache.get(cacheKey)!;
    }

    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Fetching compliance report for ${cacheKey} using ${availableBooks.length} code books`
    );

    // Create the promise and cache it immediately
    const complianceReportPromise = this.fetchComplianceReportInternal(page, availableBooks);
    this.complianceReportCache.set(cacheKey, complianceReportPromise);

    // A rejected promise is not reusable data. Bind eviction to the promise
    // lifecycle so every concurrent caller observes the same request while a
    // later Retry still performs a fresh read.
    complianceReportPromise.catch(() => {
      if (this.complianceReportCache.get(cacheKey) === complianceReportPromise) {
        this.complianceReportCache.delete(cacheKey);
      }
    });

    return complianceReportPromise;
  }

  private async fetchComplianceReportInternal(page: ArchitecturalPlanPage, codeBooks?: CodeBook[]): Promise<any> {
    try {
      // Phase 3: Use dynamically discovered books if not provided
      // Issue #484: Use page parameter directly instead of this.selectedPage
      const activeFileId = this.extractFileIdFromPage(page);
      const actualCodeBooks =
        codeBooks ||
        (await this.getAvailableCodeBooks(
          page.getArchitecturalPlanId(),
          page.getPageNumber(),
          activeFileId || undefined
        ));

      if (actualCodeBooks.length === 0) {
        ClientLoggingService.log(
          'WARN',
          `[${this.getServiceLabel()}] No available code books for compliance report for project ${page.getArchitecturalPlanId()}`
        );
        return { sections: [], getReportSummary: () => 'No code books available for this project.' };
      }

      ClientLoggingService.log(
        'INFO',
        `[${this.getServiceLabel()}] Using ${actualCodeBooks.length} dynamically discovered code books: ${actualCodeBooks.map(b => b.abbreviation).join(', ')}`
      );

      // NEW (Issue #167): Use file_id extracted from URL path instead of looking it up
      const fileId = activeFileId;

      // Get applicable sections, compliance reports, and TOCs in parallel
      const applicableSectionsPromise = this.getApplicableSections(page);

      const compliancePromises = actualCodeBooks.map(codeBook => {
        const request = new GetPageComplianceReportRequest();
        request.setPageNumber(page.getPageNumber());
        request.setArchitecturalPlanId(page.getArchitecturalPlanId());
        request.setIccBookId(codeBook.id);

        // NEW (Issue #167): Set file_id if available
        if (fileId) {
          request.setFileId(fileId);
        }

        ClientLoggingService.log(
          'INFO',
          `[${this.getServiceLabel()}] Requesting compliance report for ${codeBook.abbreviation} with:`,
          request.toObject()
        );

        return new Promise<{ response: any; codeBook: CodeBook }>((resolve, reject) => {
          // Use Promise-based API instead of callback-based to trigger AuthInterceptor
          this.reviewClient
            .getPageComplianceReport(request, null)
            .then(response => {
              resolve({ response, codeBook });
            })
            .catch(err => {
              // Phase 3: Enhanced error handling with categorization
              const errorCategory = this.categorizeComplianceError(
                err,
                `getPageComplianceReport for ${codeBook.abbreviation}`
              );

              // Log with appropriate severity
              if (errorCategory.severity === 'error') {
                ClientLoggingService.log(
                  'ERROR',
                  `[${this.getServiceLabel()}] ${errorCategory.type} error in getPageComplianceReport for ${codeBook.abbreviation}: ${errorCategory.technicalMessage}`
                );
              } else if (errorCategory.severity === 'warning') {
                ClientLoggingService.log(
                  'WARN',
                  `[${this.getServiceLabel()}] ${errorCategory.type} warning in getPageComplianceReport for ${codeBook.abbreviation}: ${errorCategory.technicalMessage}`
                );
              } else {
                ClientLoggingService.log(
                  'INFO',
                  `[${this.getServiceLabel()}] ${errorCategory.type} info in getPageComplianceReport for ${codeBook.abbreviation}: ${errorCategory.technicalMessage}`
                );
              }

              // Phase 3: Enhanced handling - distinguish between different error types
              if (
                errorCategory.type === 'NOT_FOUND' &&
                errorCategory.technicalMessage.includes('Compliance analysis must be initiated')
              ) {
                // This is expected for books with applicability but no compliance reports yet
                // Return empty compliance response (null) but don't treat as error
                ClientLoggingService.log(
                  'INFO',
                  `[${this.getServiceLabel()}] ${codeBook.abbreviation} has applicability data but no compliance reports yet (expected scenario)`
                );
                resolve({
                  response: null, // No compliance reports available
                  codeBook,
                  hasApplicabilityOnly: true, // Flag to indicate this is expected, not an error
                } as any);
              } else {
                // Do not turn a failed saved-report read into an empty report.
                // Reject so ComplianceComponent renders its recoverable error
                // state and Retry can issue a fresh RPC.
                reject(err);
              }
            });
        });
      });

      const tocPromises = actualCodeBooks.map(codeBook => this.getTableOfContents(codeBook));

      const [applicableSections, complianceResults, allChapters] = await Promise.all([
        applicableSectionsPromise,
        Promise.all(compliancePromises),
        Promise.all(tocPromises),
      ]);

      const chaptersMap = new Map<string, any>();
      allChapters.forEach((chapters, index) => {
        chaptersMap.set(actualCodeBooks[index].id, chapters);
      });

      // Process applicable sections
      const relevanceMap = new Map<string, number>();
      const rationaleMap = new Map<string, string>();
      const applicabilityModelMap = new Map<string, string>();
      const applicabilityCreatedTimeMap = new Map<string, Date>();

      if (applicableSections && applicableSections.getPageApplicabilityAnalysisList) {
        applicableSections.getPageApplicabilityAnalysisList().forEach((analysis: any) => {
          const sectionId = analysis.getIccSectionId();
          relevanceMap.set(sectionId, analysis.getRelevance());
          rationaleMap.set(sectionId, analysis.getApplicabilityRationale());
          if (analysis.getGeneratedByModel?.()) {
            applicabilityModelMap.set(sectionId, analysis.getGeneratedByModel());
          }
          if (analysis.getCreatedTime?.()) {
            const ts = analysis.getCreatedTime();
            applicabilityCreatedTimeMap.set(sectionId, new Date(ts.getSeconds() * 1000));
          }
        });
      }

      // Combine all sections from all code books
      const allSections: any[] = [];
      let combinedReportSummary = '';

      for (const result of complianceResults) {
        const { response, codeBook } = result;
        const hasApplicabilityOnly = (result as any).hasApplicabilityOnly;

        // Get table of contents for this code book from our map
        const chapters = chaptersMap.get(codeBook.id);

        // Build sections for this code book (works with null compliance response for applicability-only)
        const sections = this.buildSectionsForCodeBook(
          chapters,
          applicableSections,
          relevanceMap,
          rationaleMap,
          codeBook,
          response, // This can be null for applicability-only scenarios
          applicabilityModelMap,
          applicabilityCreatedTimeMap
        );

        if (sections.length > 0) {
          allSections.push(...sections);

          if (hasApplicabilityOnly) {
            ClientLoggingService.log(
              'INFO',
              `[${this.getServiceLabel()}] ✅ ${codeBook.abbreviation} rendered with applicability data only (${sections.length} sections)`
            );
          } else {
            ClientLoggingService.log(
              'INFO',
              `[${this.getServiceLabel()}] ✅ ${codeBook.abbreviation} rendered with full compliance data (${sections.length} sections)`
            );
          }
        }

        // Combine report summaries
        if (response && response.getReportSummary) {
          const summary = response.getReportSummary();
          if (summary) {
            combinedReportSummary +=
              (combinedReportSummary ? '\n\n' : '') + `${codeBook.abbreviation} ${codeBook.year}:\n${summary}`;
          }
        }
      }

      return { sections: allSections, getReportSummary: () => combinedReportSummary };
    } catch (error) {
      ClientLoggingService.log('ERROR', 'Error in fetchComplianceReportInternal:', error);
      throw error;
    }
  }

  private normalizeA117SectionId(sectionId: string): string {
    if (!sectionId) return '';
    const match = sectionId.match(/(\d[\d.]*)$/);
    return match ? match[1] : sectionId;
  }

  private buildSectionsForCodeBook(
    chapters: any,
    applicableSections: any,
    relevanceMap: Map<string, number>,
    rationaleMap: Map<string, string>,
    codeBook: CodeBook,
    complianceResponse: any,
    applicabilityModelMap: Map<string, string>,
    applicabilityCreatedTimeMap: Map<string, Date>
  ): any[] {
    const complianceMap = new Map<string, any>();

    // Extract compliance data if available
    if (complianceResponse) {
      try {
        const obj = complianceResponse.toObject();
        if (obj && obj.pageSectionComplianceReportsList) {
          obj.pageSectionComplianceReportsList.forEach((report: any) => {
            const sectionId = report.request?.iccSectionId;
            if (sectionId) {
              complianceMap.set(sectionId, report);
            }
          });
        }
      } catch (error) {
        ClientLoggingService.log('ERROR', `Error processing compliance response for ${codeBook.abbreviation}:`, error);
      }
    }

    // Create a set of all applicable section IDs for this code book
    const applicableSectionIds = new Set<string>();
    if (applicableSections && applicableSections.getPageApplicabilityAnalysisList) {
      applicableSections.getPageApplicabilityAnalysisList().forEach((analysis: any) => {
        const sectionId = analysis.getIccSectionId();

        // Only include sections that belong to this code book
        const documentSlug = codeBook.document_slug;
        const startsWithSlug = sectionId.startsWith(documentSlug + '_');

        // Handle special case for A117.1 which has a different ID format
        const isA117 = documentSlug.includes('icca117');
        const isA117Section = sectionId.toLowerCase().startsWith('a117');

        if (startsWithSlug || (isA117 && isA117Section)) {
          applicableSectionIds.add(sectionId);
          ClientLoggingService.log(
            'INFO',
            `[${this.getServiceLabel()}] ✅ Section ${sectionId} matches ${codeBook.abbreviation} (document_slug: ${documentSlug})`
          );
        } else {
          ClientLoggingService.log(
            'INFO',
            `[${this.getServiceLabel()}] ❌ Section ${sectionId} does NOT match ${codeBook.abbreviation} (document_slug: ${documentSlug})`
          );
        }
      });
    }

    // If no applicable sections for this code book, return empty array
    if (applicableSectionIds.size === 0) {
      return [];
    }

    // Special handling for A117.1 due to inconsistent section IDs
    if (codeBook.id === '3100') {
      const normalizedApplicabilityMap = new Map<string, any>();
      applicableSectionIds.forEach(id => {
        const analysis = applicableSections
          .getPageApplicabilityAnalysisList()
          .find((a: any) => a.getIccSectionId() === id);
        if (analysis) {
          const normalizedId = this.normalizeA117SectionId(id);
          normalizedApplicabilityMap.set(normalizedId, analysis.toObject());
        }
      });

      ClientLoggingService.log('INFO', '[DEBUG] Normalized A117.1 Applicability Map:', normalizedApplicabilityMap);

      const a117HasApplicableDescendants = (subSections: SubSection[]): boolean => {
        for (const subSection of subSections) {
          const link = subSection.getLink();
          if (link) {
            const normalizedId = this.normalizeA117SectionId(link.getId());
            const isApplicable = normalizedApplicabilityMap.has(normalizedId);
            if (isApplicable) {
              ClientLoggingService.log(
                'INFO',
                `[DEBUG] A117.1 Found Applicable Descendant: ${link.getId()} (Normalized: ${normalizedId})`
              );
              return true;
            }
          }
          if (subSection.getSubSectionsList && a117HasApplicableDescendants(subSection.getSubSectionsList())) {
            return true;
          }
        }
        return false;
      };

      const a117ProcessSubSections = (subSections: SubSection[], currentPath: any[] = []): any[] => {
        const sections: any[] = [];
        for (const subSection of subSections) {
          const link = subSection.getLink();
          if (link) {
            const sectionId = link.getId();
            const normalizedId = this.normalizeA117SectionId(sectionId);
            const applicabilityInfo = normalizedApplicabilityMap.get(normalizedId);
            const isApplicable = !!applicabilityInfo;
            const hasDescendants = subSection.getSubSectionsList && subSection.getSubSectionsList().length > 0;
            const shouldShow =
              isApplicable || (hasDescendants && a117HasApplicableDescendants(subSection.getSubSectionsList()));

            if (shouldShow) {
              const newPath = [...currentPath, { title: link.getTitle(), id: sectionId }];
              const relevance = applicabilityInfo ? this.getRelevanceLabel(applicabilityInfo.relevance) : '-';
              const status = '-'; // Compliance data not handled here for now
              const rationale = applicabilityInfo ? applicabilityInfo.applicabilityRationale : undefined;

              const section = {
                title: link.getTitle(),
                sectionId: sectionId,
                contentId: subSection.getContentId ? subSection.getContentId() : 0,
                relevance: relevance,
                status: status,
                rationale: rationale,
                depth: newPath.length - 1,
                hierarchy: newPath,
                codeBook: codeBook,
                applicabilityModel: applicabilityInfo?.generatedByModel,
                applicabilityCreatedTime: applicabilityCreatedTimeMap.get(sectionId),
              };
              sections.push(section);

              if (hasDescendants) {
                sections.push(...a117ProcessSubSections(subSection.getSubSectionsList(), newPath));
              }
            }
          }
        }
        return sections;
      };

      const sections: any[] = [];
      const bookTitle = `${codeBook.abbreviation} ${codeBook.year}`;
      const bookId = `book-${codeBook.id}`;
      sections.push({
        title: bookTitle,
        sectionId: bookId,
        depth: 0,
        hierarchy: [{ title: bookTitle, id: bookId }],
        codeBook: codeBook,
        relevance: '-',
        status: '-',
      });

      if (chapters && chapters.getChaptersList) {
        chapters.getChaptersList().forEach((chapter: Chapter) => {
          if (chapter.getSubSectionsList && a117HasApplicableDescendants(chapter.getSubSectionsList())) {
            const chapterTitle = chapter.getTitle();
            const chapterId = `${codeBook.id}-chapter-${chapter.getChapterSlug()}`;
            const chapterPath = [
              { title: bookTitle, id: bookId },
              { title: chapterTitle, id: chapterId },
            ];
            sections.push({
              title: chapterTitle,
              sectionId: chapterId,
              depth: 1,
              hierarchy: chapterPath,
              codeBook: codeBook,
              relevance: '-',
              status: '-',
            });
            sections.push(...a117ProcessSubSections(chapter.getSubSectionsList(), chapterPath));
          }
        });
      }

      return sections;
    }

    // Helper function to check if a section or its descendants are applicable
    const hasApplicableDescendants = (subSections: SubSection[]): boolean => {
      for (const subSection of subSections) {
        const link = subSection.getLink();
        if (link && applicableSectionIds.has(link.getId())) {
          return true;
        }
        if (subSection.getSubSectionsList && hasApplicableDescendants(subSection.getSubSectionsList())) {
          return true;
        }
      }
      return false;
    };

    const processSubSections = (subSections: SubSection[], currentPath: any[] = [], chapterSlug: string = '') => {
      const sections: any[] = [];
      for (const subSection of subSections) {
        const link = subSection.getLink();
        if (link) {
          const sectionId = link.getId();
          const isApplicable = applicableSectionIds.has(sectionId);
          const hasDescendants = subSection.getSubSectionsList && subSection.getSubSectionsList().length > 0;
          const shouldShow =
            isApplicable || (hasDescendants && hasApplicableDescendants(subSection.getSubSectionsList()));

          if (shouldShow) {
            const newPath = [...currentPath, { title: link.getTitle(), id: sectionId }];
            // Preserve chapterSlug from the first item in currentPath
            if (newPath.length > 1 && currentPath.length > 0 && currentPath[0].chapterSlug) {
              newPath[0] = { ...newPath[0], chapterSlug: currentPath[0].chapterSlug };
            }
            const relevance = relevanceMap.get(sectionId);
            const complianceReport = complianceMap.get(sectionId);

            const section = {
              title: link.getTitle(),
              sectionId: sectionId,
              contentId: subSection.getContentId ? subSection.getContentId() : 0,
              relevance: relevance !== undefined ? this.getRelevanceLabel(relevance) : '-',
              status: complianceReport ? this.getComplianceStatusLabel(complianceReport.complianceStatus) : '-',
              rationale: rationaleMap.get(sectionId),
              depth: newPath.length - 1,
              hierarchy: newPath,
              codeBook: codeBook,
              complianceStatement: complianceReport ? complianceReport.complianceStatement : undefined,
              complianceStatementTldr: complianceReport ? complianceReport.complianceStatementTldr : undefined,
              generatedByModel: complianceReport ? complianceReport.generatedByModel : undefined,
              createdTime:
                complianceReport && complianceReport.createdTime
                  ? new Date(complianceReport.createdTime.seconds * 1000)
                  : undefined,
              applicabilityModel: applicabilityModelMap.get(sectionId),
              applicabilityCreatedTime: applicabilityCreatedTimeMap.get(sectionId),
              // Structured evidence provenance (Issue #1773). Previously dropped here, which is why
              // the report body had no typed link model at all and had to fall back to whatever URLs
              // the model happened to write in its prose.
              //
              // The report's own request identity travels with the chain rather than being recovered
              // later from the active page. Two reasons: the chain is validated against the project
              // it belongs to, so the authorization anchor must come from the report and not from
              // wherever the reader happens to be; and cross-page evidence has to resolve against
              // the page it was found on, which is not the page whose report tab is open.
              evidenceChain: complianceReport?.evidenceChainList ?? [],
              architecturalPlanId: complianceReport?.request?.architecturalPlanId,
              evidenceFileId: complianceReport?.request?.fileId,
              evidencePageNumber: complianceReport?.request?.pageNumber,
            };

            sections.push(section);

            // Process children if they exist
            if (subSection.getSubSectionsList && subSection.getSubSectionsList().length > 0) {
              sections.push(...processSubSections(subSection.getSubSectionsList(), newPath, chapterSlug));
            }
          }
        }
      }
      return sections;
    };

    const sections: any[] = [];

    // Add the code book as the root level
    const bookTitle = `${codeBook.abbreviation} ${codeBook.year}`;
    const bookId = `book-${codeBook.id}`;

    // Always add the book section, even if it has no applicable chapters yet.
    // Orphans might be added later.
    const bookSection = {
      title: bookTitle,
      sectionId: bookId,
      contentId: 0,
      relevance: '-',
      status: '-',
      depth: 0,
      hierarchy: [{ title: bookTitle, id: bookId }],
      codeBook: codeBook,
    };
    sections.push(bookSection);

    // Process all chapters under the book
    if (chapters && chapters.getChaptersList) {
      chapters.getChaptersList().forEach((chapter: Chapter) => {
        const chapterSlug = chapter.getChapterSlug ? chapter.getChapterSlug() : '';
        const chapterTitle = chapter.getTitle();
        const chapterId = `${codeBook.id}-chapter-${chapterSlug.replace(/[^a-zA-Z0-9_-]/g, '')}`;

        // Check if this chapter has any applicable sections
        if (chapter.getSubSectionsList && hasApplicableDescendants(chapter.getSubSectionsList())) {
          const chapterSection = {
            title: chapterTitle,
            sectionId: chapterId,
            contentId: chapter.getContentId ? chapter.getContentId() : 0,
            relevance: '-',
            status: '-',
            depth: 1,
            hierarchy: [
              { title: bookTitle, id: bookId },
              { title: chapterTitle, id: chapterId, chapterSlug: chapterSlug },
            ],
            codeBook: codeBook,
          };
          sections.push(chapterSection);

          // Process subsections with updated hierarchy
          if (chapter.getSubSectionsList) {
            const subsections = this.processChapterSubSections(
              chapter.getSubSectionsList(),
              [
                { title: bookTitle, id: bookId },
                { title: chapterTitle, id: chapterId, chapterSlug: chapterSlug },
              ],
              chapterSlug,
              codeBook,
              applicableSectionIds,
              relevanceMap,
              rationaleMap,
              complianceMap,
              hasApplicableDescendants,
              processSubSections
            );

            sections.push(...subsections);
          }
        }
      });
    }

    return sections;
  }

  private processChapterSubSections(
    subSections: any[],
    currentPath: any[],
    chapterSlug: string,
    codeBook: CodeBook,
    applicableSectionIds: Set<string>,
    relevanceMap: Map<string, number>,
    rationaleMap: Map<string, string>,
    complianceMap: Map<string, any>,
    hasApplicableDescendants: (subSections: any[]) => boolean,
    processSubSections: (subSections: any[], currentPath: any[], chapterSlug: string) => any[]
  ): any[] {
    const sections: any[] = [];

    // Check if this looks like a SubChapter structure (FLBC) or direct Section structure (IBC)
    // SubChapters typically have titles like "Subchapter 01" or similar and contain sections
    const hasSubChapterStructure = subSections.some(subSection => {
      const link = subSection.getLink();
      if (!link) return false;
      const title = link.getTitle();
      const hasSubSections = subSection.getSubSectionsList && subSection.getSubSectionsList().length > 0;
      // Check if this looks like a SubChapter (has subsections and title suggests it's a container)
      return (
        hasSubSections &&
        (title.toLowerCase().includes('subchapter') ||
          title.toLowerCase().includes('sub-chapter') ||
          title.toLowerCase().includes('subcategory') ||
          // Also check if the ID suggests it's a SubChapter (like FLBC2023P2_Ch01_SubCh01)
          link.getId().includes('_SubCh'))
      );
    });

    if (hasSubChapterStructure) {
      // FLBC-style: Process SubChapters first, then their sections
      subSections.forEach(subSection => {
        const link = subSection.getLink();
        if (link) {
          const subChapterId = link.getId();
          const subChapterTitle = link.getTitle();
          const hasDescendants = subSection.getSubSectionsList && subSection.getSubSectionsList().length > 0;
          const shouldShow = hasDescendants && hasApplicableDescendants(subSection.getSubSectionsList());

          if (shouldShow) {
            // Add the SubChapter as a container
            const subChapterSection = {
              title: subChapterTitle,
              sectionId: subChapterId,
              contentId: subSection.getContentId ? subSection.getContentId() : 0,
              relevance: '-',
              status: '-',
              depth: currentPath.length, // depth relative to book level
              hierarchy: [...currentPath, { title: subChapterTitle, id: subChapterId }],
              codeBook: codeBook,
            };
            sections.push(subChapterSection);

            // Process sections within this SubChapter
            if (subSection.getSubSectionsList) {
              const subChapterSections = processSubSections(
                subSection.getSubSectionsList(),
                [...currentPath, { title: subChapterTitle, id: subChapterId }],
                chapterSlug
              );
              sections.push(...subChapterSections);
            }
          }
        }
      });
    } else {
      // IBC-style: Process sections directly (no SubChapter level)
      const directSections = processSubSections(subSections, currentPath, chapterSlug);
      sections.push(...directSections);
    }

    return sections;
  }

  private findSectionInChapters(chapters: Chapters, sectionId: string): { title: string; hierarchy: any[] } | null {
    const findInSubSections = (
      subSections: SubSection[],
      currentPath: any[] = []
    ): { title: string; hierarchy: any[] } | null => {
      for (const subSection of subSections) {
        const link = subSection.getLink();
        if (link && link.getId() === sectionId) {
          return {
            title: link.getTitle(),
            hierarchy: [...currentPath, { title: link.getTitle(), id: link.getId() }],
          };
        }
        if (subSection.getSubSectionsList) {
          const result = findInSubSections(subSection.getSubSectionsList(), [
            ...currentPath,
            { title: link?.getTitle() || '', id: link?.getId() || '' },
          ]);
          if (result) return result;
        }
      }
      return null;
    };

    for (const chapter of chapters.getChaptersList()) {
      const chapterTitle = chapter.getTitle();
      const chapterId = chapter.getSubSectionsList()[0]?.getLink()?.getId() || '';
      const result = findInSubSections(chapter.getSubSectionsList(), [{ title: chapterTitle, id: chapterId }]);
      if (result) return result;
    }
    return null;
  }

  private getRelevanceLabel(relevance: number): string {
    switch (relevance) {
      case 0:
        return 'Not Relevant';
      case 1:
        return 'High';
      case 2:
        return 'Medium';
      case 3:
        return 'Low';
      default:
        return '-';
    }
  }

  private getComplianceStatusLabel(status: number): string {
    switch (status) {
      case 1:
        return 'Non-Compliant';
      case 2:
        return 'Not Applicable';
      case 3:
        return 'Compliant';
      case 4:
        return 'Partially Compliant';
      default:
        return 'Unknown';
    }
  }

  clearComplianceReportCache(): void {
    this.complianceReportCache.clear();
  }

  clearComplianceReportCacheForPage(page: ArchitecturalPlanPage): void {
    const fileId = this.extractFileIdFromPage(page) || 'legacy';
    const baseCacheKey = `${page.getArchitecturalPlanId()}-${fileId}-${page.getPageNumber()}`;
    ClientLoggingService.log(
      'INFO',
      `[${this.getServiceLabel()}] Clearing compliance report cache for ${baseCacheKey}*`
    );

    const keysToDelete = Array.from(this.complianceReportCache.keys()).filter(key => key.startsWith(baseCacheKey));
    keysToDelete.forEach(key => this.complianceReportCache.delete(key));
  }

  /**
   * Example method demonstrating progress tracking with cold start detection
   * This method wraps a gRPC call with progress tracking
   */
  getArchitecturalPlanWithProgress(planId: string): Observable<any> {
    const request = new GetArchitecturalPlanRequest();
    request.setArchitecturalPlanId(planId);

    const grpcCall = this.client.getArchitecturalPlan(request, null);

    return this.grpcWrapper.wrapWithProgress(grpcCall, 'Loading architectural plan...', 'Plan loaded successfully');
  }

  /**
   * Generate compliance reports for multiple sections in parallel.
   * Creates individual tasks for each section to enable parallel processing.
   *
   * @param projectId The architectural project ID
   * @param pageNumber The page number to analyze (1-based)
   * @param sectionIds Array of ICC section IDs to generate reports for
   * @param bookId The ICC book ID (e.g., "2217" for IBC 2021)
   * @returns Promise with task IDs, success status, and message
   */
  async generatePageSectionComplianceReports(
    projectId: string,
    pageNumber: number,
    sectionIds: string[],
    bookId: string,
    fileId?: string,
    forceRecompute?: boolean
  ): Promise<{ taskIds: string[]; success: boolean; message: string }> {
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Starting compliance report generation:`, {
      projectId,
      pageNumber,
      sectionIds: sectionIds.length,
      bookId,
    });

    try {
      const taskIds: string[] = [];
      const errorMessages: string[] = [];

      // Create individual tasks for each section to enable parallelization
      for (const sectionId of sectionIds) {
        ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Creating task for section: ${sectionId}`);

        // Create request for the new async RPC
        const request = new StartPageSectionComplianceReportTaskRequest();
        request.setArchitecturalPlanId(projectId);
        request.setPageNumber(pageNumber);
        request.setIccBookId(bookId);
        request.setIccSectionId(sectionId);

        // NEW (Issue #167): Set file_id for hierarchical file structure
        if (fileId) {
          request.setFileId(fileId);
          ClientLoggingService.log(
            'INFO',
            `[${this.getServiceLabel()}] Using file_id=${fileId} for compliance report (modern structure)`
          );
        } else {
          ClientLoggingService.log(
            'INFO',
            `[${this.getServiceLabel()}] No file_id provided for compliance report (legacy mode)`
          );
        }

        if (forceRecompute) {
          request.setForceRecompute(true);
        }

        try {
          // Call the async compliance report RPC
          const response = await this.reviewClient.startPageSectionComplianceReportTask(request, null);
          const responseObj = response.toObject();

          if (responseObj.success && responseObj.taskId) {
            taskIds.push(responseObj.taskId);
            ClientLoggingService.log(
              'INFO',
              `[${this.getServiceLabel()}] Task created successfully for section ${sectionId}: ${responseObj.taskId}`
            );
          } else {
            const errorMsg = responseObj.message || 'Unknown error';
            errorMessages.push(errorMsg);
            ClientLoggingService.log(
              'ERROR',
              `[${this.getServiceLabel()}] Failed to create task for section ${sectionId}: ${errorMsg}`
            );
          }
        } catch (sectionError) {
          const errorMsg = sectionError instanceof Error ? sectionError.message : String(sectionError);
          errorMessages.push(errorMsg);
          ClientLoggingService.log(
            'ERROR',
            `[${this.getServiceLabel()}] Error creating task for section ${sectionId}:`,
            sectionError
          );
        }
      }

      const successCount = taskIds.length;
      const totalCount = sectionIds.length;

      if (successCount === 0) {
        const detail = extractUserFacingErrorDetail(errorMessages);
        return {
          taskIds: [],
          success: false,
          message: detail || `Failed to start any compliance report tasks (0/${totalCount})`,
        };
      } else if (successCount < totalCount) {
        return {
          taskIds,
          success: true,
          message: `Started ${successCount}/${totalCount} compliance report tasks (${totalCount - successCount} failed)`,
        };
      } else {
        return {
          taskIds,
          success: true,
          message: `Started ${successCount} compliance report tasks successfully`,
        };
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', `[${this.getServiceLabel()}] Error starting compliance reports:`, error);
      return {
        taskIds: [],
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Generate a single compliance report for a specific section.
   * This is a convenience method for single-section reports.
   *
   * @param projectId The architectural project ID
   * @param pageNumber The page number to analyze (1-based)
   * @param sectionId The ICC section ID to generate report for
   * @param bookId The ICC book ID (e.g., "2217" for IBC 2021)
   * @returns Promise with task ID, success status, and message
   */
  async generateSinglePageSectionComplianceReport(
    projectId: string,
    pageNumber: number,
    sectionId: string,
    bookId: string
  ): Promise<{ taskId: string | null; success: boolean; message: string }> {
    ClientLoggingService.log('INFO', `[${this.getServiceLabel()}] Starting single compliance report generation:`, {
      projectId,
      pageNumber,
      sectionId,
      bookId,
    });

    const result = await this.generatePageSectionComplianceReports(projectId, pageNumber, [sectionId], bookId);

    return {
      taskId: result.taskIds.length > 0 ? result.taskIds[0] : null,
      success: result.success,
      message: result.message,
    };
  }

  updateArchitecturalPlanPageMetadata(
    request: UpdateArchitecturalPlanPageMetadataRequest
  ): Promise<UpdateArchitecturalPlanPageMetadataResponse> {
    return this.client.updateArchitecturalPlanPageMetadata(request, null);
  }

  groomArchitecturalPlanPageMetadata(
    request: GroomArchitecturalPlanPageMetadataRequest
  ): Promise<GroomArchitecturalPlanPageMetadataResponse> {
    return this.client.groomArchitecturalPlanPageMetadata(request, null);
  }
}
